import { define } from 'wicked-elements';
import Bowser from 'bowser';

const NOTIFICATION_PROMPT_KEY = 'gpla-prompt:notifications'
const { promptDelay, promptQuiet } = window.OneSignalInternalOptions || {};

define('[data-component="notification-prompt"]', {
  mobileOnly: false, // Set to false to allow on all devices
  standaloneOnly: true, // Set to true to only show in standalone mode
  
  init() {
    console.log('[NotificationPrompt] Initializing component.');
    
    // Check if OneSignal is intentionally not loaded (no app ID)
    if (window.OneSignalFallback) {
      console.log('[NotificationPrompt] OneSignal intentionally not loaded (no app ID), removing component.');
      this.element.remove();
      return;
    }
    
    if (!this.shouldShowPrompt()) {
      this.element.remove();
      return;
    }
    
    this.isThankYouState = false;
    this.setupOneSignalListeners();
  },

  connected() {
    // Component is connected to DOM
  },

  disconnected() {
    // Clean up OneSignal listeners if needed
    if (this.oneSignalChangeListener) {
      window.OneSignalDeferred.push((OneSignal) => {
        OneSignal.User.PushSubscription.removeEventListener("change", this.oneSignalChangeListener);
      });
    }
  },

  getBrowserInfo() {
    if (!this._browserInfo) {
      this._browserInfo = Bowser.getParser(window.navigator.userAgent)
    }
    return this._browserInfo
  },

  isMobileDevice() {
    const browser = this.getBrowserInfo()
    const platform = browser.getPlatformType()
    return platform === 'mobile' || platform === 'tablet'
  },

  isStandaloneMode() {
    return window.matchMedia('(display-mode: standalone)').matches 
      || window.matchMedia('(display-mode: minimal-ui)').matches
      || window.navigator.standalone
  },

  shouldShowPrompt() {
    // Check mobile-only flag
    if (this.mobileOnly && !this.isMobileDevice()) {
      console.log('[NotificationPrompt] Mobile-only flag enabled, not showing on desktop.');
      return false;
    }
    
    // Check standalone-only flag
    if (this.standaloneOnly && !this.isStandaloneMode()) {
      console.log('[NotificationPrompt] Standalone-only flag enabled, not showing in browser mode.');
      return false;
    }
    
    // Always check the dismissal timestamp first, regardless of permission state
    const dismissedTimestamp = localStorage.getItem(NOTIFICATION_PROMPT_KEY);
    
    if (dismissedTimestamp) {
      const dismissedTime = parseInt(dismissedTimestamp);
      const currentTime = Date.now();
      const timeSinceDismissed = currentTime - dismissedTime;
      
      // If less than one week has passed since dismissal, don't show
      if (timeSinceDismissed < promptDelay) {
        console.log('[NotificationPrompt] Prompt dismissed recently, not showing yet.');
        return false;
      }
    }
    
    // Only show if permission is 'default' (never asked) or 'denied' (blocked)
    // Don't show if permission is 'granted' (user has enabled notifications)
    if (promptQuiet && Notification.permission === 'granted') {
      console.log('[NotificationPrompt] Permission already granted. Removing prompt.');
      return false;
    }
    
    return true;
  },

  setupOneSignalListeners() {
    window.OneSignalDeferred.push((OneSignal) => {
      console.log('[NotificationPrompt] OneSignal SDK is ready.');

      // Check if user is already opted in
      if (OneSignal.User.PushSubscription.optedIn) {
        console.log('[NotificationPrompt] User already opted in. Removing prompt.');
        this.element.remove();
        return;
      }
      
      // Only render the banner after confirming user is not subscribed
      this.renderBanner();
      
      // The main listener. Fires when the user Allows or Blocks permission.
      this.oneSignalChangeListener = () => {
        console.log('[NotificationPrompt] PushSubscription "change" event fired.');

        // Check the definitive browser permission state.
        const newPermission = Notification.permission;
        console.log(`[NotificationPrompt] Native permission state is now: ${newPermission}`);

        if (newPermission === 'granted') {
          // This case is for when the user clicks 'Allow'.
          this.setDismissed(); // Set timestamp so they won't see prompt again for a week
          this.showThankYouMessage();
        } else if (newPermission === 'denied') {
          // This case is for when the user clicks 'Block'.
          this.setDismissed(); // Set timestamp so they won't see prompt again for a week
          console.log('[NotificationPrompt] User blocked notifications, removing prompt.');
          this.element.remove();
        }
        // If newPermission is 'default', the user dismissed the prompt, and this
        // event listener does not fire, which is the desired behavior.
      };
      
      OneSignal.User.PushSubscription.addEventListener("change", this.oneSignalChangeListener);
    });
  },

  primaryBanner({
    primaryLabel = 'Enable',
    secondaryLabel = 'Maybe Later'
  } = {}) {
    return `
      <button class="absolute top-0 right-0 p-3 text-xl leading-none" data-dismiss>×</button>
      <div class="flex flex-col gap-x-8 gap-y-4">
        <div class="w-full flex items-center gap-3 pointer-events-none select-none">
          <div class="w-11 h-11 bg-white border rounded-lg flex-none self-start flex items-center justify-center">
            <img src="/assets/images/pwa/icon_bell.svg" alt="" class="w-6 h-6" />
          </div>
          <div class="pt-0.5 text-lg">
            <h3 class="font-bold">Stay Updated</h3>
            <p>Enable push notifications for updates</p>
          </div>
        </div>
        <div class="w-full flex gap-3">
          <button
            class="flex-1 ui-button ui-button--sm h-11 text-base flex items-center justify-center relative bg-white hover:bg-yellow"
            data-primary
          >
            <span class="absolute left-0 right-0 text-center w-full">${primaryLabel}</span>
          </button>
          <button class="flex-1 ui-button ui-button--sm h-11 text-base border-neutral-400 hover:border-black transition-colors" data-secondary>${secondaryLabel}</button>
        </div>
      </div>
    `
  },

  setDismissed() {
    localStorage.setItem(NOTIFICATION_PROMPT_KEY, Date.now().toString());
  },

  renderBanner() {
    this.element.innerHTML = `
      <div class="bg-neutral-50 border border-black p-3 relative rounded-lg">
        ${this.primaryBanner({
          primaryLabel: 'Enable',
          secondaryLabel: 'Maybe Later'
        })}
      </div>
    `;
    
    this.enableButton = this.element.querySelector('[data-primary]');
    this.laterButton = this.element.querySelector('[data-secondary]');
    this.closeButton = this.element.querySelector('[data-dismiss]');

    this.enableButton.addEventListener('click', () => this.prompt());
    this.laterButton.addEventListener('click', () => this.dismissPrompt());
    this.closeButton.addEventListener('click', () => this.dismissPrompt());
  },

  prompt() {
    console.log('[NotificationPrompt] Prompting user for subscription via optIn().');
    window.OneSignalDeferred.push((OneSignal) => {
      OneSignal.User.PushSubscription.optIn();
    });
  },

  dismissPrompt() {
    this.setDismissed();
    this.element.remove();
  },

  showThankYouMessage() {
    console.log('[NotificationPrompt] Showing thank you message.');
    this.isThankYouState = true;
    this.element.innerHTML = `
      <div class="bg-neutral-50 border p-3 relative rounded-lg">
        <button class="absolute top-0 right-0 p-3 text-xl leading-none" data-dismiss>×</button>
        <div class="flex items-center gap-3">
          <div class="w-11 h-11 bg-white border rounded-lg flex-none self-start flex items-center justify-center">
            <img src="/assets/images/pwa/icon_check.svg" alt="" class="w-6 h-6" />
          </div>
          <div class="pt-0.5 text-lg">
            <h3 class="font-bold">Thank you!</h3>
            <p>You are now subscribed to notifications.</p>
          </div>
        </div>
      </div>
    `;

    // Add event listener for the dismiss button
    this.closeButton = this.element.querySelector('[data-dismiss]');
    this.closeButton.addEventListener('click', () => this.element.remove());

    // Auto-remove the "Thank You" banner after 5 seconds
    setTimeout(() => {
      console.log('[NotificationPrompt] Auto-removing thank you message.');
      this.element.remove();
    }, 5000);
  }
});
